import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Label } from "@src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/components/ui/select";
import { cn } from "@src/lib/utils";
import { useField } from "formik";
import { useId } from "react";

export interface FormikSelectOption<T extends string = string> {
  label: string;
  value: T;
}

export interface FormikSelectProps<T extends string = string> {
  name: string;
  label?: string;
  items: FormikSelectOption<T>[];
  placeholder?: string;
  required?: boolean;
  containerClassName?: string;
  disabled?: boolean;
}

/**
 * Formik-integrated Select built on the shadcn Select primitive. Replaces the
 * legacy `<Select3Formik />` used in login and create-account flows.
 */
export function FormikSelect<T extends string = string>({
  name,
  label,
  items,
  placeholder,
  required,
  containerClassName,
  disabled,
}: FormikSelectProps<T>) {
  const [field, meta, helpers] = useField<T>(name);
  const error = meta.touched && meta.error ? meta.error : undefined;
  const id = useId();

  return (
    <VerticalFlexbox gap="xs" className={cn("w-full", containerClassName)}>
      {label ? (
        <Label htmlFor={id} className="text-sm text-foreground">
          {label}
          {required ? (
            <span className="ml-0.5 text-destructive" aria-hidden>
              *
            </span>
          ) : null}
        </Label>
      ) : null}
      <Select
        value={field.value}
        onValueChange={(value) => helpers.setValue(value as T)}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          className={cn(error && "border-destructive ring-destructive")}
          aria-invalid={error ? "true" : undefined}
        >
          <SelectValue placeholder={placeholder ?? "Select an option"} />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </VerticalFlexbox>
  );
}
